import React, { useState, useEffect } from 'react';
import { X, CreditCard, Loader2, AlertCircle, MapPin, User, Ambulance } from 'lucide-react';
import { v1Api } from '../../../services/api';
import PaymentAmountDisplay from '../components/PaymentAmountDisplay';

/**
 * PaymentInitiationModal Component
 * Endpoint: POST /v1/hospital/bookings/{bookingId}/initiate-payment
 * Purpose: Initiates payment for a booking after confirmation
 */
const PaymentInitiationModal = ({
  isOpen,
  onClose,
  bookingId,
  bookingData,
  estimatedFare,
  patientName,
  pickupAddress,
  destinationAddress,
  ambulanceType,
  onPaymentSuccess,
  onError
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [razorpayScriptLoaded, setRazorpayScriptLoaded] = useState(false);

  // Load Razorpay script
  useEffect(() => {
    if (isOpen && !razorpayScriptLoaded) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        setRazorpayScriptLoaded(true);
        console.log('Razorpay script loaded successfully');
      };
      script.onerror = () => {
        setError('Failed to load Razorpay SDK. Please refresh the page.');
      };
      document.body.appendChild(script);
    }
  }, [isOpen, razorpayScriptLoaded]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      requestAnimationFrame(() => {
        document.body.style.overflow = 'hidden';
      });
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      requestAnimationFrame(() => {
        document.body.style.overflow = 'unset';
      });
    };
  }, [isOpen, loading, onClose]);

  const handleInitiatePayment = async () => {
    if (!bookingId || !estimatedFare) {
      const errMsg = 'Missing booking information. Please try again.';
      setError(errMsg);
      if (onError) onError(errMsg);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Extract fare value
      let fareValue = estimatedFare;
      if (typeof fareValue === 'string') {
        fareValue = parseFloat(fareValue.replace(/[₹,\s]/g, ''));
      } else if (typeof fareValue === 'object' && fareValue?.toString) {
        fareValue = parseFloat(fareValue.toString());
      }

      if (isNaN(fareValue) || fareValue <= 0) {
        throw new Error('Invalid fare amount');
      }

      // Format fare as string with 2 decimal places
      const fareString = fareValue.toFixed(2);

      // Generate idempotency key in format: YYYYMMDDHHMM-MM-DD
      // Example format: "7660152025-11-21" (based on provided example)
      // Using timestamp-based format for uniqueness
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      // Format: YYYYMMDDHHMM-MM-DD (e.g., "202501211145-01-21")
      // This ensures uniqueness per minute
      const idempotencyKey = `${year}${month}${day}${hours}${minutes}-${month}-${day}`;

      // Prepare request body
      const requestBody = {
        idempotencyKey: idempotencyKey,
        fare: fareString
      };

      console.log('💳 [PaymentInitiationModal] Initiating payment for booking:', bookingId);
      console.log('💳 [PaymentInitiationModal] Request body:', requestBody);
      console.log('💳 [PaymentInitiationModal] Endpoint: POST /v1/hospital/bookings/' + bookingId + '/initiate-payment');

      // Call the initiate-payment endpoint
      // Endpoint: POST /v1/hospital/bookings/:bookingId/initiate-payment
      const endpoint = `/v1/hospital/bookings/${bookingId}/initiate-payment`;
      const response = await v1Api.post(endpoint, requestBody);
      
      // Handle response
      // Expected response structure:
      // {
      //   "success": true,
      //   "message": "Payment initiated successfully for booking",
      //   "data": {
      //     "order": {
      //       "id": "order_RsAkCwZE58Yvor",
      //       "amount": 1185.07,
      //       "currency": "INR",
      //       "receipt": "order_1765863943187_c0d701",
      //       "status": "CREATED"
      //     },
      //     "keyId": "rzp_test_RbYLX9Fx48IwWy",
      //     "isExisting": true
      //   }
      // }
      const result = response.data || response;

      console.log('💳 [PaymentInitiationModal] Full response:', result);

      // Validate response structure
      if (!result?.success) {
        const errorMsg = result?.message || result?.error || 'Payment initiation failed';
        throw new Error(errorMsg);
      }

      if (!result?.data) {
        throw new Error('Invalid response: missing data field');
      }

      // Extract response data
      const { order, keyId, isExisting } = result.data;

      if (!order) {
        throw new Error('Invalid response: missing order information');
      }

      if (!order?.id) {
        throw new Error('Invalid response: missing order ID');
      }

      if (!keyId) {
        throw new Error('Invalid response: missing Razorpay key ID');
      }

      console.log('💳 [PaymentInitiationModal] Order created:', order.id);
      console.log('💳 [PaymentInitiationModal] Order amount:', order.amount);
      console.log('💳 [PaymentInitiationModal] Order currency:', order.currency);
      console.log('💳 [PaymentInitiationModal] Order status:', order.status);
      console.log('💳 [PaymentInitiationModal] Razorpay Key ID:', keyId);
      console.log('💳 [PaymentInitiationModal] Is Existing Order:', isExisting);

      // Note: order.amount is in rupees (not paise) based on the response format
      // Razorpay expects amount in paise, so we need to convert
      const orderAmountInPaise = Math.round(parseFloat(order.amount) * 100);

      // Open Razorpay checkout with converted amount
      openRazorpayCheckout({ ...order, amount: orderAmountInPaise }, keyId);
    } catch (err) {
      console.error('❌ [PaymentInitiationModal] Payment initiation error:', err);
      console.error('❌ [PaymentInitiationModal] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      const errMsg = err.response?.data?.message || 
                    err.response?.data?.error || 
                    err.message || 
                    'Failed to initiate payment. Please try again.';
      setError(errMsg);
      setLoading(false);
      if (onError) onError(errMsg);
    }
  };

  const openRazorpayCheckout = (order, keyIdToUse) => {
    if (!window.Razorpay) {
      const errMsg = 'Razorpay SDK not loaded. Please refresh the page.';
      setError(errMsg);
      setLoading(false);
      if (onError) onError(errMsg);
      return;
    }

    const handlePaymentSuccess = (response) => {
      console.log('Payment successful:', response);
      setLoading(false);
      if (onPaymentSuccess) {
        onPaymentSuccess({
          paymentId: response.razorpay_payment_id,
          signature: response.razorpay_signature,
          orderId: order.id,
          orderData: order
        });
      }
      onClose();
    };

    const onPaymentDismiss = () => {
      setLoading(false);
      const errMsg = 'Payment was cancelled. Please try again.';
      setError(errMsg);
      if (onError) onError(errMsg);
    };

    const options = {
      key: keyIdToUse,
      amount: order.amount, // Amount in paise (already converted)
      currency: order.currency || 'INR',
      name: 'Ambulance Service',
      description: `Payment for Booking ${bookingId}`,
      order_id: order.id,
      handler: handlePaymentSuccess,
      prefill: {
        name: patientName || 'User',
        contact: bookingData?.patientPhone || '',
        email: bookingData?.patientEmail || ''
      },
      theme: {
        color: '#667eea'
      },
      modal: {
        ondismiss: onPaymentDismiss
      }
    };

    try {
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error('Error opening Razorpay checkout:', err);
      const errMsg = 'Failed to open payment gateway. Please try again.';
      setError(errMsg);
      setLoading(false);
      if (onError) onError(errMsg);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg max-w-md w-full mx-auto shadow-xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="bg-blue-50 p-4 rounded-t-lg border-b border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-blue-100 p-1.5 rounded-full">
                <CreditCard className="w-4 h-4 text-blue-600" />
              </div>
              <div className="ml-2">
                <h3 className="text-base font-semibold text-blue-900">
                  Complete Payment
                </h3>
                <p className="text-xs text-blue-700">
                  Secure payment via Razorpay
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-blue-400 hover:text-blue-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Booking Summary */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 mb-3">Booking Summary</h4>
            <div className="space-y-2">
              {patientName && (
                <div className="flex items-center gap-2 text-xs">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-500">Patient:</span>
                  <span className="text-gray-900 font-medium">{patientName}</span>
                </div>
              )}
              {ambulanceType && (
                <div className="flex items-center gap-2 text-xs">
                  <Ambulance className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-500">Ambulance:</span>
                  <span className="text-gray-900 font-medium">{ambulanceType}</span>
                </div>
              )}
              {pickupAddress && (
                <div className="flex items-start gap-2 text-xs">
                  <MapPin className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-gray-500">Pickup:</span>
                    <span className="text-gray-900 font-medium ml-1">{pickupAddress}</span>
                  </div>
                </div>
              )}
              {destinationAddress && (
                <div className="flex items-start gap-2 text-xs">
                  <MapPin className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-gray-500">Destination:</span>
                    <span className="text-gray-900 font-medium ml-1">{destinationAddress}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Amount Display */}
          {estimatedFare && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <div className="text-center">
                <p className="text-xs font-medium text-green-800 mb-2">Amount to Pay</p>
                <PaymentAmountDisplay amount={estimatedFare} size="large" />
                <p className="text-xs text-green-600 mt-2">
                  Secure payment via Razorpay
                </p>
              </div>
            </div>
          )}

          {/* Security Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800">
                <p className="font-medium mb-1">Secure Payment</p>
                <p className="text-blue-600">
                  Your payment is processed securely through Razorpay. We do not store your card details.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 rounded-b-lg">
          <div className="flex justify-between items-center gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleInitiatePayment}
              disabled={loading || !estimatedFare || !razorpayScriptLoaded}
              className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md transition-colors flex items-center justify-center gap-2 ${
                loading || !estimatedFare || !razorpayScriptLoaded
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  Proceed to Payment
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentInitiationModal;

